import { loadImage } from "@hg/recall-frame"

import shareFrameImage from "../../../../assets/imgs/recall/share/frame.png"
import shareBgImage from "../../../../assets/imgs/recall/share/share.png"

const DESIGN_WIDTH = 1352
const DESIGN_HEIGHT = 807
const CANVAS_WIDTH = DESIGN_WIDTH * 2
const CANVAS_HEIGHT = DESIGN_HEIGHT * 2

const FONT_SANS = '"PingFang SC", "Microsoft YaHei", sans-serif'
const FONT_NAME = '"HuXiaoBo-WeiWuSong-Jian"'

const CG_PADDING = 4
const CG_RECT = {
    x: 120,
    y: 100,
    width: 721,
    height: 398,
}
const QRCODE_RECT = {
    x: 152,
    y: 538,
    width: 134,
    height: 134,
}
const NICKNAME_RECT = {
    x: 307,
    y: 559,
    width: 462,
    height: 51,
}
const CODE_RECT = {
    x: 307,
    y: 610,
    width: 462,
    height: 40,
}

type Rect = {
    x: number
    y: number
    width: number
    height: number
}

type LayoutMetrics = {
    scale: number
    offsetX: number
    offsetY: number
}

type DrawStrokeTextOptions = {
    text: string
    rect: Rect
    fontSize: number
    fontFamily: string
    fontWeight?: string
    fillColor: string
    strokeColor: string
    strokeWidth: number
    minFontSize?: number
}

export interface DrawRecallShareImageOptions {
    cg: string
    qrCodeUrl: string
    nickname: string
    code?: string
}

export interface DrawRecallShareImageResult {
    canvas: HTMLCanvasElement
    base64: string
}

const createLayoutMetrics = (): LayoutMetrics => {
    const scale = Math.min(
        CANVAS_WIDTH / DESIGN_WIDTH,
        CANVAS_HEIGHT / DESIGN_HEIGHT,
    )

    return {
        scale,
        offsetX: (CANVAS_WIDTH - DESIGN_WIDTH * scale) / 2,
        offsetY: (CANVAS_HEIGHT - DESIGN_HEIGHT * scale) / 2,
    }
}

const scaleValue = (value: number, metrics: LayoutMetrics) =>
    value * metrics.scale

const scaleRect = (rect: Rect, metrics: LayoutMetrics): Rect => ({
    x: metrics.offsetX + scaleValue(rect.x, metrics),
    y: metrics.offsetY + scaleValue(rect.y, metrics),
    width: scaleValue(rect.width, metrics),
    height: scaleValue(rect.height, metrics),
})

const createCanvas = () => {
    const canvas = document.createElement("canvas")
    canvas.width = CANVAS_WIDTH
    canvas.height = CANVAS_HEIGHT
    return canvas
}

const drawRectImage = (
    ctx: CanvasRenderingContext2D,
    image: CanvasImageSource,
    rect: Rect,
) => {
    ctx.drawImage(image, rect.x, rect.y, rect.width, rect.height)
}

const drawCoverImage = (
    ctx: CanvasRenderingContext2D,
    image: { width: number; height: number } & CanvasImageSource,
    rect: Rect,
) => {
    const sourceAspect = image.width / image.height
    const targetAspect = rect.width / rect.height

    let sourceWidth = image.width
    let sourceHeight = image.height
    let sourceX = 0
    let sourceY = 0

    if (sourceAspect > targetAspect) {
        sourceWidth = image.height * targetAspect
        sourceX = (image.width - sourceWidth) / 2
    } else {
        sourceHeight = image.width / targetAspect
        sourceY = (image.height - sourceHeight) / 2
    }

    ctx.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        rect.x,
        rect.y,
        rect.width,
        rect.height,
    )
}

const loadFont = async (font: string, text: string) => {
    if (typeof document === "undefined" || !document.fonts) {
        return
    }

    try {
        if (!document.fonts.check(font, text)) {
            await document.fonts.load(font, text)
        }
    } catch {
        // Ignore font loading failures and render with the current browser state.
    }
}

const createRoundedRectPath = (
    ctx: CanvasRenderingContext2D,
    rect: Rect,
    radius: number,
) => {
    const safeRadius = Math.min(radius, rect.width / 2, rect.height / 2)

    ctx.beginPath()
    ctx.moveTo(rect.x + safeRadius, rect.y)
    ctx.arcTo(
        rect.x + rect.width,
        rect.y,
        rect.x + rect.width,
        rect.y + rect.height,
        safeRadius,
    )
    ctx.arcTo(
        rect.x + rect.width,
        rect.y + rect.height,
        rect.x,
        rect.y + rect.height,
        safeRadius,
    )
    ctx.arcTo(rect.x, rect.y + rect.height, rect.x, rect.y, safeRadius)
    ctx.arcTo(rect.x, rect.y, rect.x + rect.width, rect.y, safeRadius)
    ctx.closePath()
}

const drawRoundedImage = (
    ctx: CanvasRenderingContext2D,
    image: CanvasImageSource,
    rect: Rect,
    radius: number,
) => {
    ctx.save()
    createRoundedRectPath(ctx, rect, radius)
    ctx.clip()
    drawRectImage(ctx, image, rect)
    ctx.restore()
}

const fitFontSize = (
    ctx: CanvasRenderingContext2D,
    text: string,
    options: {
        fontFamily: string
        fontWeight?: string
        initialSize: number
        minSize: number
        maxWidth: number
    },
) => {
    const {
        fontFamily,
        fontWeight = "normal",
        initialSize,
        minSize,
        maxWidth,
    } = options

    for (let size = initialSize; size >= minSize; size -= 1) {
        ctx.font = `${fontWeight} ${size}px ${fontFamily}`
        if (ctx.measureText(text).width <= maxWidth) {
            return size
        }
    }

    return minSize
}

const wrapTextByWord = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
): string[] => {
    const lines: string[] = []
    let currentLine = ""

    for (const char of Array.from(text)) {
        const nextLine = currentLine + char
        if (ctx.measureText(nextLine).width <= maxWidth) {
            currentLine = nextLine
        } else if (!currentLine) {
            currentLine = nextLine
        } else {
            const lastSpace = currentLine.lastIndexOf(" ")
            if (lastSpace > 0) {
                lines.push(currentLine.slice(0, lastSpace))
                currentLine = currentLine.slice(lastSpace + 1) + char
            } else {
                lines.push(currentLine)
                currentLine = char
            }
        }
    }
    if (currentLine) lines.push(currentLine)
    return lines
}

const drawWrappedStrokeText = (
    ctx: CanvasRenderingContext2D,
    metrics: LayoutMetrics,
    options: DrawStrokeTextOptions,
) => {
    const { text, rect, fontSize, fontFamily, fontWeight, fillColor, strokeColor, strokeWidth } = options
    const scaledRect = scaleRect(rect, metrics)
    const scaledStrokeWidth = scaleValue(strokeWidth, metrics)
    const maxTextWidth = scaledRect.width - scaledStrokeWidth * 2
    const initialSize = Math.round(scaleValue(fontSize, metrics))
    const minSize = Math.round(scaleValue(options.minFontSize ?? fontSize * 0.7, metrics))

    // 先尝试缩字号让文本单行放入
    const singleLineFontSize = fitFontSize(ctx, text, {
        fontFamily,
        fontWeight,
        initialSize,
        minSize,
        maxWidth: maxTextWidth,
    })

    ctx.font = `${fontWeight || "normal"} ${singleLineFontSize}px ${fontFamily}`
    const fits = ctx.measureText(text).width <= maxTextWidth

    let lines: string[]
    let resolvedFontSize: number

    if (fits) {
        lines = [text]
        resolvedFontSize = singleLineFontSize
    } else {
        // 缩到最小字号后仍超宽，改为换行
        resolvedFontSize = minSize
        ctx.font = `${fontWeight || "normal"} ${resolvedFontSize}px ${fontFamily}`
        lines = wrapTextByWord(ctx, text, maxTextWidth)
    }

    const lineHeight = resolvedFontSize * 1.25
    const centerX = scaledRect.x + scaledRect.width / 2
    // 单行居中于 rect，多行从 rect 顶部开始向下排列
    const startY = lines.length === 1
        ? scaledRect.y + scaledRect.height / 2
        : scaledRect.y + resolvedFontSize / 2

    ctx.save()
    ctx.font = `${fontWeight || "normal"} ${resolvedFontSize}px ${fontFamily}`
    ctx.fillStyle = fillColor
    ctx.strokeStyle = strokeColor
    ctx.lineWidth = scaledStrokeWidth * 2
    ctx.lineJoin = "round"
    ctx.lineCap = "round"
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"

    for (let i = 0; i < lines.length; i++) {
        const y = startY + i * lineHeight
        ctx.strokeText(lines[i], centerX, y)
        ctx.fillText(lines[i], centerX, y)
    }

    ctx.restore()
}

const drawStrokeText = (
    ctx: CanvasRenderingContext2D,
    metrics: LayoutMetrics,
    options: DrawStrokeTextOptions,
) => {
    const {
        text,
        rect,
        fontSize,
        fontFamily,
        fontWeight,
        fillColor,
        strokeColor,
        strokeWidth,
    } = options
    const scaledRect = scaleRect(rect, metrics)
    const scaledStrokeWidth = scaleValue(strokeWidth, metrics)
    const maxTextWidth = scaledRect.width - scaledStrokeWidth * 2
    const resolvedFontSize = fitFontSize(ctx, text, {
        fontFamily,
        fontWeight,
        initialSize: Math.round(scaleValue(fontSize, metrics)),
        minSize: Math.round(
            scaleValue(options.minFontSize ?? fontSize * 0.7, metrics),
        ),
        maxWidth: maxTextWidth,
    })

    ctx.save()
    ctx.font = `${fontWeight || "normal"} ${resolvedFontSize}px ${fontFamily}`
    ctx.fillStyle = fillColor
    ctx.strokeStyle = strokeColor
    ctx.lineWidth = scaledStrokeWidth * 2
    ctx.lineJoin = "round"
    ctx.lineCap = "round"
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"

    const centerX = scaledRect.x + scaledRect.width / 2
    const centerY = scaledRect.y + scaledRect.height / 2

    ctx.strokeText(text, centerX, centerY)
    ctx.fillText(text, centerX, centerY)
    ctx.restore()
}

const loadAssets = async (options: DrawRecallShareImageOptions) => {
    const [shareBg, shareFrame, cgImage, qrCodeImage] = await Promise.all([
        loadImage(shareBgImage),
        loadImage(shareFrameImage),
        loadImage(options.cg),
        loadImage(options.qrCodeUrl),
    ])

    return {
        shareBg,
        shareFrame,
        cgImage,
        qrCodeImage,
    }
}

export const drawRecallShareImage = async (
    options: DrawRecallShareImageOptions,
): Promise<DrawRecallShareImageResult> => {
    const canvas = createCanvas()
    const ctx = canvas.getContext("2d")

    if (!ctx) {
        throw new Error("2d canvas context is not available")
    }

    const metrics = createLayoutMetrics()
    const assets = await loadAssets(options)

    await Promise.all([
        loadFont(
            `bold ${Math.round(scaleValue(30, metrics))}px ${FONT_SANS}`,
            options.nickname,
        ),
        options.code
            ? loadFont(
                  `normal ${Math.round(scaleValue(34, metrics))}px ${FONT_NAME}`,
                  options.code,
              )
            : Promise.resolve(),
    ])

    drawRectImage(
        ctx,
        assets.shareBg,
        scaleRect(
            {
                x: 0,
                y: 0,
                width: DESIGN_WIDTH,
                height: DESIGN_HEIGHT,
            },
            metrics,
        ),
    )

    drawCoverImage(
        ctx,
        assets.cgImage,
        scaleRect(
            {
                x: CG_RECT.x + CG_PADDING,
                y: CG_RECT.y + CG_PADDING,
                width: CG_RECT.width - CG_PADDING * 2,
                height: CG_RECT.height - CG_PADDING * 2,
            },
            metrics,
        ),
    )
    drawRectImage(ctx, assets.shareFrame, scaleRect(CG_RECT, metrics))
    drawRoundedImage(
        ctx,
        assets.qrCodeImage,
        scaleRect(QRCODE_RECT, metrics),
        scaleValue(8, metrics),
    )

    drawWrappedStrokeText(ctx, metrics, {
        text: options.nickname,
        rect: NICKNAME_RECT,
        fontSize: 30,
        fontFamily: FONT_SANS,
        fontWeight: "bold",
        fillColor: "#6c461d",
        strokeColor: "#fffaed",
        strokeWidth: 3,
        minFontSize: 22,
    })

    if (options.code) {
        drawStrokeText(ctx, metrics, {
            text: options.code,
            rect: CODE_RECT,
            fontSize: 34,
            fontFamily: FONT_NAME,
            fillColor: "#ffffff",
            strokeColor: "#946e0f",
            strokeWidth: 2,
            minFontSize: 26,
        })
    }

    return {
        canvas,
        base64: canvas.toDataURL("image/png"),
    }
}
